import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCart } from '@/lib/cart';
import { fmtMoney, fmtTimeAgo } from '@/lib/realtime';
import { ORDER_STATUS_COLORS, getContextualStatusLabel } from '@/lib/orderStatus';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';
import { Repeat, AlertCircle, RefreshCw, ShoppingBag, MapPin, Store, Star } from 'lucide-react';
import { toast } from 'sonner';
import FeedbackPrompt from '@/components/FeedbackPrompt';

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState({}); // id -> branch
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackOrder, setFeedbackOrder] = useState(null);
  const nav = useNavigate();
  const { add, clear } = useCart();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, b] = await Promise.all([
        api.get('/orders/mine'),
        api.get('/branches').catch(() => ({ data: [] })),
      ]);
      setOrders(Array.isArray(r.data) ? r.data : []);
      const byId = {};
      for (const br of (b.data || [])) byId[br.id] = br;
      setBranches(byId);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not load your orders right now.';
      setError(typeof msg === 'string' ? msg : 'Could not load your orders right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // NOTE: We intentionally do NOT auto-open the "Rate your order" prompt for
  // customers anymore. Frequent diners reported that the popup was nagging
  // them every visit (even after logging out and back in). Ratings are now
  // strictly opt-in via the per-order "Rate" button below.

  const orderAgain = async (order) => {
    try {
      // Re-fetch latest menu items to ensure availability
      const { data: items } = await api.get('/menu/items');
      const byId = new Map(items.map((i) => [i.id, i]));
      clear();
      let added = 0;
      for (const it of order.items || []) {
        const m = byId.get(it.menu_item_id);
        if (m && m.is_available) {
          add(m, it.quantity, it.special_instructions || '');
          added += 1;
        }
      }
      if (added) {
        toast.success(`Added ${added} item(s) to cart`);
        nav('/checkout');
      } else {
        toast.error('No items from that order are available anymore.');
      }
    } catch (e) { toast.error('Failed to add items'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold">My orders</h1>
            <p className="text-sm text-muted-foreground">Live status, history and digital receipts.</p>
          </div>
          <Button data-testid="my-orders-refresh-button" size="sm" variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        <div className="mt-6 grid gap-3">
          {loading && [0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-secondary" />
          ))}
          {!loading && error && (
            <Card className="border-rose-200 bg-rose-50/50" data-testid="my-orders-error">
              <CardContent className="flex flex-col items-center gap-2 p-6 text-center">
                <AlertCircle className="h-8 w-8 text-rose-600" />
                <p className="font-semibold text-rose-900">{error}</p>
                <p className="text-xs text-rose-700">Please check your connection and try again.</p>
                <Button size="sm" className="mt-2" onClick={load}>
                  <RefreshCw className="mr-1 h-4 w-4" /> Retry
                </Button>
              </CardContent>
            </Card>
          )}
          {!loading && !error && !orders.length && (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
                  <ShoppingBag className="h-7 w-7 text-muted-foreground" />
                </span>
                <p className="font-display text-xl font-semibold">No orders yet</p>
                <p className="text-sm text-muted-foreground">When you place your first order, it'll show up here with live status updates.</p>
                <Button onClick={() => nav('/menu')}>Browse menu</Button>
              </CardContent>
            </Card>
          )}
          {!loading && !error && orders.map((o) => {
            const branch = o.branch_id ? branches[o.branch_id] : null;
            return (
            <Card key={o.id} data-testid="my-orders-row" className="transition-shadow hover:shadow-md">
              <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="cursor-pointer flex-1" onClick={() => nav(`/order/${o.id}`)}>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold">#{o.order_number}</h3>
                    <Badge className={`border ${ORDER_STATUS_COLORS[o.status] || ''}`}>{getContextualStatusLabel(o.status, o.order_type, o.table_number)}</Badge>
                    <span className="text-xs text-muted-foreground capitalize">{o.order_type?.replace('_', ' ')}</span>
                  </div>
                  {branch && (
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                      <span data-testid="my-orders-branch-name" className="inline-flex items-center gap-1 font-medium text-foreground">
                        <Store className="h-3 w-3 text-primary" /> {branch.name}
                      </span>
                      {branch.address && (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <MapPin className="h-3 w-3" /> {branch.address}
                        </span>
                      )}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">{fmtTimeAgo(o.created_at)} · {o.items?.length} item(s)</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-lg font-semibold tabular">{fmtMoney(o.total)}</p>
                    <p className="text-xs text-muted-foreground capitalize">{o.payment_method} · {o.payment_status}</p>
                  </div>
                  <Button data-testid="my-orders-reorder-button" size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); orderAgain(o); }}>
                    <Repeat className="mr-1 h-4 w-4" /> Order again
                  </Button>
                  {['delivered', 'completed'].includes(o.status) && (
                    <Button data-testid="my-orders-feedback-button" size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setFeedbackOrder(o); }}>
                      <Star className="mr-1 h-4 w-4" /> Rate
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      </main>
      <FeedbackPrompt
        open={!!feedbackOrder}
        onOpenChange={(o) => { if (!o) setFeedbackOrder(null); }}
        orderId={feedbackOrder?.id}
        source="post_delivery"
        authed
        title="Rate your order"
        description={feedbackOrder ? `How was order #${feedbackOrder.order_number}?` : ''}
      />
      <Footer />
    </div>
  );
}
